/**
 * `?unstale` — the URL escape hatch, and the one piece of it both halves of
 * the app have to agree on.
 *
 * Two independent recoveries hang off this one query parameter, and either
 * alone is enough (`docs/engineering/offline-caching-design.md`):
 *
 * - **In the worker** (`sw.ts`): a navigation whose URL carries it is answered
 *   straight from the network, with no cache read at all. That recovers a page
 *   whose *cached shell* is broken, even if the worker itself is fine.
 * - **In the page** (`register-sw.ts`): seeing it on boot runs `unstale()` —
 *   unregister every worker, delete every cache, reload. That recovers a
 *   *broken worker*, even one that never passes the parameter through.
 *
 * It exists because the `Ctrl`+`Shift`+`Backspace` chord cannot be typed on an
 * iPad, and because a URL is the one instruction a user can be given over the
 * phone.
 *
 * Its own module so `sw.ts` can have the constant without importing the page's
 * `unstale.ts` (which reaches for `window` and `navigator`), and so the two
 * halves cannot drift apart on the spelling.
 */

export const UNSTALE_PARAM = "unstale";

/** Whether a `location.search` asks for the escape hatch. Total: a malformed query is a no. */
export function hasUnstaleFlag(search: string): boolean {
  try {
    return new URLSearchParams(search).has(UNSTALE_PARAM);
  } catch {
    return false;
  }
}

/**
 * The same URL with the flag removed.
 *
 * The page half has to strip it *before* `unstale()` reloads, or the reload
 * arrives carrying `?unstale` again and the app clears its cache on every load
 * for ever — a self-inflicted version of the lockout this hatch exists to fix.
 * Returns the input unchanged if it cannot be parsed, which is the safe way to
 * fail: at worst the hatch runs twice.
 */
export function withoutUnstaleFlag(href: string): string {
  try {
    const url = new URL(href);
    url.searchParams.delete(UNSTALE_PARAM);
    // `URLSearchParams` leaves a bare `?` behind once the last key is gone.
    return url.search === "" ? `${url.origin}${url.pathname}${url.hash}` : url.toString();
  } catch {
    return href;
  }
}
